import { demoFunc } from './dees-form-submit.demo.js';
import {
  customElement,
  html,
  DeesElement,
  css,
  cssManager,
  property,
  type TemplateResult,
} from '@design.estate/dees-element';
import type { DeesForm } from '../dees-form/dees-form.js';
import { themeDefaultStyles } from '../../00theme.js';

declare global {
  interface HTMLElementTagNameMap {
    'dees-form-submit': DeesFormSubmit;
  }
}

@customElement('dees-form-submit')
export class DeesFormSubmit extends DeesElement {
  public static demo = demoFunc;
  public static demoGroups = ['Form', 'Button'];

  // =============================================
  // Properties forwarded to internal dees-button
  // =============================================

  @property({ type: Boolean, reflect: true })
  accessor disabled = false;

  @property({ type: String })
  accessor text!: string;

  @property({ type: String })
  accessor status: 'normal' | 'pending' | 'success' | 'error' = 'normal';

  @property({ type: String, reflect: true })
  accessor type: 'default' | 'accent' | 'secondary' | 'destructive' | 'outline' | 'ghost' | 'link' | 'normal' | 'highlighted' | 'discreet' | 'big' = 'default';

  @property({ type: String, reflect: true })
  accessor size: 'sm' | 'default' | 'lg' | 'icon' | 'small' | 'normal' | 'large' = 'default';

  @property({ type: String })
  accessor icon!: string;

  @property({ type: String })
  accessor iconPosition: 'left' | 'right' = 'left';

  constructor() {
    super();
  }

  public static styles = [
    themeDefaultStyles,
    cssManager.defaultStyles,
    css`
      :host {
        display: inline-block;
      }
      dees-button {
        width: 100%;
      }
    `,
  ];

  public render(): TemplateResult {
    return html`
      <dees-button
        .status=${this.status}
        .type=${this.type}
        .size=${this.size}
        .icon=${this.icon}
        .iconPosition=${this.iconPosition}
        .text=${this.text}
        ?disabled=${this.disabled}
        @clicked=${this.submit}
      ></dees-button>
    `;
  }

  public async firstUpdated() {
    // Capture light DOM text content as the button label. dees-button wipes
    // its own light DOM during extractLightDom(), so we cannot simply forward
    // a <slot> into it — we have to hoist the text onto the .text property
    // ourselves before handing it to dees-button.
    if (!this.text) {
      const slotText = this.textContent?.trim();
      if (slotText) {
        this.text = slotText;
      }
    }
  }

  public async submit() {
    if (this.disabled) {
      return;
    }
    // Walk up the DOM tree to find the nearest dees-form element
    const parentFormElement = this.closest('dees-form') as DeesForm;
    if (parentFormElement && parentFormElement.gatherAndDispatch) {
      parentFormElement.gatherAndDispatch();
    }
  }

  public async focus() {
    const domtools = await this.domtoolsPromise;
    if (!this.disabled) {
      await domtools.convenience.smartdelay.delayFor(0);
      this.submit();
    }
  }
}
